import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Pencil, Trash2 } from 'lucide-react'
import { PageHeader } from '@/components/ui/PageHeader'
import { Toolbar } from '@/components/ui/Toolbar'
import { DataTable, type DataTableColumn } from '@/components/ui/DataTable'
import { Pagination } from '@/components/ui/Pagination'
import { Button } from '@/components/ui/Button'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { StatusDot } from '@/components/ui/StatusDot'
import { useCrudList } from '@/hooks/useCrudList'
import { useDataTableExport } from '@/hooks/useDataTableExport'
import { useStatusFilter } from '@/hooks/useStatusFilter'
import { createCrudService } from '@/services/api/createCrudService'
import { toast } from '@/components/ui/Toast'
import { extractApiError } from '@/lib/api-errors'

interface Branch {
  id: string
  companyId: string
  systemName: string
  corporateName: string | null
  cpfCnpj: string | null
  type: string
  email: string | null
  phone: string | null
  mobile: string | null
  status: 'ATIVO' | 'INATIVO'
  formatted?: { cpfCnpj?: string; phone?: string; mobile?: string }
}

const BranchesService = createCrudService<Branch>('/my-company/branches', { searchField: 'system_name' })

export function MyCompanyBranchesPage() {
  const navigate = useNavigate()
  const [reload, setReload] = useState(0)
  const sf = useStatusFilter({ searchPlaceholder: 'Buscar filial' })
  const { setPage, size, setSize, rows, meta, loading } = useCrudList(BranchesService, { size: 25, reloadKey: reload, filters: sf.filters })
  const [openDelete, setOpenDelete] = useState<Branch | null>(null)

  const exp = useDataTableExport({
    filename: 'filiais',
    service: BranchesService,
    filters: sf.filters,
    columns: [
      { header: 'Nome',         accessor: (b: Branch) => b.systemName },
      { header: 'Razão social', accessor: (b: Branch) => b.corporateName ?? '' },
      { header: 'CPF/CNPJ',     accessor: (b: Branch) => b.formatted?.cpfCnpj ?? b.cpfCnpj ?? '' },
      { header: 'E-mail',       accessor: (b: Branch) => b.email ?? '' },
      { header: 'Celular',      accessor: (b: Branch) => b.formatted?.mobile ?? b.mobile ?? '' },
      { header: 'Telefone',     accessor: (b: Branch) => b.formatted?.phone ?? b.phone ?? '' },
      { header: 'Status',       accessor: (b: Branch) => b.status },
    ],
  })

  async function handleDelete() {
    if (!openDelete) return
    try {
      await BranchesService.destroy(openDelete.id)
      toast.success({ title: 'Removida', description: 'Filial removida com sucesso.' })
      setOpenDelete(null)
      setReload(v => v + 1)
    } catch (e) {
      toast.error({ title: 'Não foi possível remover', description: extractApiError(e).message })
    }
  }

  const columns: DataTableColumn<Branch>[] = [
    { key: 'systemName',    header: 'Nome', sortable: true, alwaysVisible: true,
      render: b => <span className="font-medium text-gray-800">{b.systemName}</span> },
    { key: 'corporateName', header: 'Razão social', align: 'center', render: b => b.corporateName ?? '—' },
    { key: 'cpfCnpj',       header: 'Documento', align: 'center', className: 'w-44',
      render: b => <span className="text-xs">{b.formatted?.cpfCnpj ?? b.cpfCnpj ?? '—'}</span> },
    { key: 'contact',       header: 'Contato', align: 'center', render: b => (
      <span className="text-xs text-gray-500">{b.formatted?.mobile ?? b.formatted?.phone ?? '—'}</span>
    )},
    { key: 'status', header: 'Status', align: 'center', className: 'w-24',
      render: b => (
        <StatusDot value={b.status} />
      ),
    },
  ]

  return (
    <>
      <PageHeader
        title="Filiais"
        subtitle="Filiais da minha empresa"
        crumbs={[{ label: 'Minha empresa' }, { label: 'Filiais' }]}
        actions={<Button onClick={() => navigate('/my-company/branches/novo')} iconLeft={<Plus size={14} />}>Nova filial</Button>}
      />
      <Toolbar onFiltersClick={sf.open} activeFiltersCount={sf.activeCount} />
      <DataTable
        tableId="mycompany.branches"
        exportable
        onExportPage={() => exp.exportPage(rows)}
        onExportAll={exp.exportAll}
        columns={columns}
        rows={rows}
        loading={loading}
        getRowKey={r => r.id}
        rowActions={(b) => [
          { key: 'edit',   label: 'Editar',  icon: <Pencil size={14} />, onClick: () => navigate(`/my-company/branches/${b.id}/editar`) },
          { key: 'delete', label: 'Remover', icon: <Trash2 size={14} />, danger: true, divider: true, onClick: () => setOpenDelete(b) },
        ]}
      />
      <Pagination meta={meta} onChange={setPage} size={size} onSizeChange={setSize} />

      <ConfirmDialog
        open={!!openDelete}
        onClose={() => setOpenDelete(null)}
        onConfirm={handleDelete}
        title="Remover filial?"
        description={`Tem certeza que deseja remover "${openDelete?.systemName}"? Esta ação não pode ser desfeita.`}
        confirmLabel="Remover"
        variant="danger"
      />
      {sf.drawer}
    </>
  )
}
